import { useState, useEffect } from 'react'
import type { EAPipelineTaskListItem } from '../../types/ea'
import EAVendorBadge from './EAVendorBadge'

interface EATaskCardProps {
  task: EAPipelineTaskListItem
  onCancel?: (id: string) => void
  onRetry?: (id: string) => void
}

const TASK_STATUS_COLORS: Record<string, string> = {
  pending: '#6b7280',
  queued: '#3b82f6',
  running: '#f59e0b',
  completed: '#10b981',
  failed: '#ef4444',
  blocked: '#f97316',
  cancelled: '#9ca3af',
}

const TASK_STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  queued: 'Queued',
  running: 'Running',
  completed: 'Completed',
  failed: 'Failed',
  blocked: 'Blocked',
  cancelled: 'Cancelled',
}

const TASK_TYPE_LABELS: Record<string, string> = {
  orchestrator: 'Orchestrator',
  vendor_pipeline: 'Vendor Pipeline',
  requirements: 'Requirements',
  design: 'Design',
  standards_gate: 'Standards Gate',
  deployment: 'Deployment',
  qc: 'QC',
  deliverables_promotion: 'Deliverables',
}

const AGENT_ROLE_LABELS: Record<string, string> = {
  ea_orchestrator: 'EA Orchestrator',
  vendor_orchestrator: 'Vendor Orchestrator',
  architect: 'Architect',
  design_engineer: 'Design Engineer',
  deployment_engineer: 'Deployment Engineer',
  qc_validation: 'QC Validation',
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`
  const mins = Math.floor(ms / 60000)
  const secs = Math.floor((ms % 60000) / 1000)
  return `${mins}m ${secs}s`
}

function ElapsedTimer({ startedAt }: { startedAt: string }) {
  const [elapsed, setElapsed] = useState(0)

  useEffect(() => {
    const start = new Date(startedAt).getTime()
    const update = () => setElapsed(Date.now() - start)
    update()
    const interval = setInterval(update, 1000)
    return () => clearInterval(interval)
  }, [startedAt])

  return <span>{formatDuration(elapsed)}</span>
}

export default function EATaskCard({ task, onCancel, onRetry }: EATaskCardProps) {
  const statusColor = TASK_STATUS_COLORS[task.status] || '#6b7280'
  const statusLabel = TASK_STATUS_LABELS[task.status] || task.status
  const typeLabel = TASK_TYPE_LABELS[task.task_type] || task.task_type
  const roleLabel = task.agent_role ? AGENT_ROLE_LABELS[task.agent_role] || task.agent_role : null
  const isRunning = task.status === 'running'
  const isFailed = task.status === 'failed'
  const canCancel = task.status === 'running' || task.status === 'pending' || task.status === 'queued'
  const canRetry = task.status === 'failed'

  return (
    <div
      className="rounded-xl p-5 transition-all duration-200"
      style={{
        background: 'rgba(30, 41, 59, 0.5)',
        border: `1px solid ${isFailed ? 'rgba(239, 68, 68, 0.3)' : isRunning ? 'rgba(245, 158, 11, 0.3)' : 'rgba(100, 180, 255, 0.1)'}`,
      }}
    >
      {/* Header: title + status badge */}
      <div className="flex items-start justify-between mb-3">
        <h4 className="text-sm font-semibold leading-tight" style={{ color: '#e2e8f0' }}>
          {task.title}
        </h4>
        <span
          className={`flex-shrink-0 ml-3 px-2.5 py-1 text-xs font-medium rounded-full inline-flex items-center space-x-1.5 ${isRunning ? 'animate-pulse' : ''}`}
          style={{
            background: `${statusColor}22`,
            color: statusColor,
            border: `1px solid ${statusColor}44`,
          }}
        >
          {isRunning && (
            <span
              className="w-1.5 h-1.5 rounded-full"
              style={{ background: statusColor }}
            />
          )}
          <span>{statusLabel}</span>
        </span>
      </div>

      {/* Badges row: task type, vendor, agent role */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <span
          className="px-2 py-0.5 text-xs font-medium rounded-md"
          style={{
            background: 'rgba(139, 92, 246, 0.15)',
            color: '#a78bfa',
            border: '1px solid rgba(139, 92, 246, 0.3)',
          }}
        >
          {typeLabel}
        </span>
        {task.vendor && <EAVendorBadge vendor={task.vendor} size="sm" />}
        {roleLabel && (
          <span
            className="px-2 py-0.5 text-xs font-medium rounded-md"
            style={{
              background: 'rgba(6, 182, 212, 0.15)',
              color: '#67e8f9',
              border: '1px solid rgba(6, 182, 212, 0.3)',
            }}
          >
            {roleLabel}
          </span>
        )}
      </div>

      {/* Duration / elapsed time */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3 text-xs" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>
          {isRunning && task.started_at && (
            <span className="flex items-center space-x-1">
              <i className="fa-solid fa-clock"></i>
              <ElapsedTimer startedAt={task.started_at} />
            </span>
          )}
          {task.status === 'completed' && task.duration_ms != null && (
            <span className="flex items-center space-x-1">
              <i className="fa-solid fa-stopwatch"></i>
              <span>{formatDuration(task.duration_ms)}</span>
            </span>
          )}
          {task.started_at && (
            <span>
              Started {new Date(task.started_at).toLocaleTimeString()}
            </span>
          )}
          {task.completed_at && (
            <span>
              Completed {new Date(task.completed_at).toLocaleTimeString()}
            </span>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex items-center space-x-2">
          {canCancel && onCancel && (
            <button
              onClick={() => onCancel(task.id)}
              className="px-2.5 py-1 text-xs font-medium rounded-lg transition-all duration-200 hover:scale-105"
              style={{
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.2)',
                color: '#f87171',
              }}
            >
              Cancel
            </button>
          )}
          {canRetry && onRetry && (
            <button
              onClick={() => onRetry(task.id)}
              className="px-2.5 py-1 text-xs font-medium rounded-lg transition-all duration-200 hover:scale-105"
              style={{
                background: 'rgba(59, 130, 246, 0.1)',
                border: '1px solid rgba(59, 130, 246, 0.2)',
                color: '#60a5fa',
              }}
            >
              Retry
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
